import React, { useEffect, useState } from 'react';
import { X, Plus, DownloadCloud } from 'lucide-react';
import { OrganizationService } from '../../services/organizationService';

interface AttachableRepo {
  id: string;
  name: string;
  fullName: string;
  platform: string;
  latestAnalysisScore: number | null;
}

export const AddRepositoriesModal: React.FC<{
  orgId: string;
  onClose: () => void;
  onAttached: () => void;
}> = ({ orgId, onClose, onAttached }) => {
  const [repos, setRepos] = useState<AttachableRepo[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Import a platform org/group's public repos — provider per import,
  // since an Autar org can mix providers.
  const [importPlatform, setImportPlatform] = useState('github');
  const [importSlug, setImportSlug] = useState('');
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState<string | null>(null);

  useEffect(() => {
    OrganizationService.listAttachable(orgId)
      .then((res) => setRepos(res.data.repositories))
      .catch(() => setRepos([]))
      .finally(() => setLoading(false));
  }, [orgId]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const attach = async () => {
    if (selected.size === 0) return;
    setSaving(true);
    try {
      await OrganizationService.attachRepositories(orgId, [...selected]);
      onAttached();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const importOrg = async () => {
    const slug = importSlug.trim();
    if (!slug || importing) return;
    setImporting(true);
    setImportMsg(null);
    try {
      const res = await OrganizationService.importFromPlatform(orgId, importPlatform, slug);
      const { fetched, created, updated } = res.data;
      setImportMsg(
        `Imported ${fetched} public repos from ${importPlatform}/${slug} (${created} new, ${updated} updated)`,
      );
      onAttached();
    } catch (e) {
      setImportMsg(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <h2 className="font-semibold text-gray-900 dark:text-white">Add repositories</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Import a whole platform org/group (public repos, read-only) */}
        <div className="px-5 pt-4 pb-3 border-b border-gray-100 dark:border-gray-800">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Import from a platform org
          </p>
          <div className="flex items-center gap-2">
            <select
              value={importPlatform}
              onChange={(e) => setImportPlatform(e.target.value)}
              className="w-36 shrink-0 px-2 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-transparent text-gray-900 dark:text-white"
            >
              <option value="github">GitHub org</option>
              <option value="gitlab">GitLab group</option>
            </select>
            <input
              value={importSlug}
              onChange={(e) => setImportSlug(e.target.value)}
              placeholder="handle (e.g. dowjones)"
              className="flex-1 min-w-0 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-transparent text-gray-900 dark:text-white"
            />
            <button
              onClick={importOrg}
              disabled={!importSlug.trim() || importing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/30 disabled:opacity-40"
            >
              <DownloadCloud className="w-4 h-4" />
              {importing ? 'Importing…' : 'Import'}
            </button>
          </div>
          <p className="mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">
            {importMsg ??
              'Public repositories only, read-only. Private repos via OAuth org connect (coming soon).'}
          </p>
        </div>

        <div className="p-5 max-h-96 overflow-y-auto">
          {loading ? (
            <p className="text-sm text-gray-500">Loading your repositories…</p>
          ) : repos.length === 0 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 space-y-2">
              <p>All of your repositories are already in this fleet.</p>
              <p className="text-xs">
                Use “Import from a platform org” above to pull a whole org's public repos, or
                analyze individual repositories to make them attachable here.
              </p>
            </div>
          ) : (
            <ul className="space-y-1">
              {repos.map((r) => (
                <li key={r.id}>
                  <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/40 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggle(r.id)}
                      className="h-4 w-4 shrink-0 accent-teal-600"
                    />
                    <span className="flex-1 text-sm text-gray-900 dark:text-gray-200">
                      {r.fullName || r.name}
                    </span>
                    <span className="text-xs text-gray-400">{r.platform}</span>
                    <span className="text-xs text-gray-500 w-8 text-right">
                      {r.latestAnalysisScore ?? '—'}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-800">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={attach}
            disabled={selected.size === 0 || saving}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-40"
          >
            <Plus className="w-4 h-4" />
            {saving ? 'Adding…' : `Add ${selected.size || ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};
